// PWA update discovery for long-lived sessions (#1494). The browser only
// byte-compares the service-worker script on a page LOAD, and an installed PWA
// is usually suspended rather than reloaded — one device sat on a 24-day-stale
// bundle, reporting crashes that had long since been fixed. This asks the
// registration to check again when the app comes back to the foreground, and
// on a slow interval while it stays open.
//
// Discovery only: a found worker waits, and `PwaUpdatePrompt`'s existing
// `needRefresh` Snackbar takes over exactly as on a fresh load. Pairing this
// with `registerType: "autoUpdate"` is ruled out (#1083 — see web-pwa-seo.md).
//
// Plain `.ts` with injected deps, like applyWaitingSwAndReload.ts, so it
// unit-tests without jsdom timing or the ts-jest `import.meta` trap.

export const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;
// A tab-switch burst must not turn into a burst of update() calls. The load
// itself just checked, so the first foreground return inside this gap is a
// no-op too.
export const UPDATE_CHECK_MIN_GAP_MS = 60 * 1000;

type VisibilityTarget = {
  readonly visibilityState: DocumentVisibilityState;
  addEventListener: (type: "visibilitychange", listener: () => void) => void;
  removeEventListener: (type: "visibilitychange", listener: () => void) => void;
};

export type UpdateDiscoveryDeps = {
  doc: VisibilityTarget;
  isOnline: () => boolean;
  now: () => number;
  setInterval: (fn: () => void, ms: number) => number;
  clearInterval: (id: number) => void;
  warn: (message: string, error: unknown) => void;
};

const defaultDeps = (): UpdateDiscoveryDeps => ({
  doc: document,
  isOnline: () => navigator.onLine,
  now: () => Date.now(),
  setInterval: (fn, ms) => window.setInterval(fn, ms),
  clearInterval: (id) => window.clearInterval(id),
  warn: (message, error) => console.warn(message, error),
});

/** Starts discovery; returns a disposer that removes the listener and timer. */
export const startUpdateDiscovery = (
  reg: { update: () => Promise<unknown> },
  deps: UpdateDiscoveryDeps = defaultDeps(),
): (() => void) => {
  let lastCheck = deps.now();

  const check = (): void => {
    if (deps.doc.visibilityState !== "visible") return;
    // Offline the byte-compare can only fail; the next foreground return or
    // tick after reconnecting does the work.
    if (!deps.isOnline()) return;
    const now = deps.now();
    if (now - lastCheck < UPDATE_CHECK_MIN_GAP_MS) return;
    lastCheck = now;
    // The .catch is load-bearing: an unawaited rejection would reach the
    // global handler and be reported to Sentry as a crash.
    reg.update().catch((error: unknown) => {
      deps.warn("[updateDiscovery] service worker update check failed", error);
    });
  };

  deps.doc.addEventListener("visibilitychange", check);
  const intervalId = deps.setInterval(check, UPDATE_CHECK_INTERVAL_MS);
  return () => {
    deps.doc.removeEventListener("visibilitychange", check);
    deps.clearInterval(intervalId);
  };
};
